/*
 * Free-gift removal confirmation dialog scoped to a single cart line.
 *
 * Exposes window.__confirmFreeGiftRemoval(productTitle, anchorEl) → Promise.
 * Instead of a full-page modal, the confirmation is injected INSIDE the
 * anchorEl's closest `.cart-item` row (or a sensible fallback ancestor) and
 * overlays only that row. Keeps shopper context: they see the gift they're
 * about to lose and the prompt without the whole cart dimming.
 *
 * Resolves `true` when the shopper picks "Remove anyway", `false` on
 * "Keep my gift", Escape, or clicking the scoped backdrop.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var state = {
    host: null,
    root: null,
    dialog: null,
    titleEl: null,
    keepBtn: null,
    removeBtn: null,
    resolve: null,
    lastActive: null,
    keydownHandler: null,
    open: false,
  };

  function buildOverlay() {
    var root = document.createElement('div');
    root.className = 'gift-remove-confirm gift-remove-confirm--scoped';
    root.setAttribute('hidden', '');

    var dialog = document.createElement('div');
    dialog.className = 'gift-remove-confirm__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'gift-remove-confirm-title');
    dialog.tabIndex = -1;

    var title = document.createElement('p');
    title.className = 'gift-remove-confirm__title';
    title.id = 'gift-remove-confirm-title';

    var actions = document.createElement('div');
    actions.className = 'gift-remove-confirm__actions';

    var keepBtn = document.createElement('button');
    keepBtn.type = 'button';
    keepBtn.className = 'button gift-remove-confirm__keep';
    keepBtn.textContent = 'Keep my gift';

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'button button--tertiary gift-remove-confirm__remove';
    removeBtn.textContent = 'Remove anyway';

    actions.appendChild(keepBtn);
    actions.appendChild(removeBtn);
    dialog.appendChild(title);
    dialog.appendChild(actions);
    root.appendChild(dialog);

    // Clicking the scoped backdrop (anywhere inside root but outside the
    // dialog) dismisses as a "keep". The dialog click handler stops
    // propagation so clicks inside the card don't bubble and close.
    root.addEventListener('click', function (e) {
      if (!dialog.contains(e.target)) close(false);
    });

    keepBtn.addEventListener('click', function () {
      close(false);
    });
    removeBtn.addEventListener('click', function () {
      close(true);
    });

    state.root = root;
    state.dialog = dialog;
    state.titleEl = title;
    state.keepBtn = keepBtn;
    state.removeBtn = removeBtn;
  }

  function resolveHost(anchorEl) {
    if (!anchorEl) return null;
    // Prefer the cart line row so the overlay tracks with scroll. Fall back
    // to cart-item__details (table cell), then the anchor's parent.
    return (
      anchorEl.closest('.cart-item') ||
      anchorEl.closest('[data-cart-item]') ||
      anchorEl.closest('.cart-item__details') ||
      anchorEl.parentElement
    );
  }

  function mountInto(host) {
    if (!host) return false;
    if (state.host === host && state.root && state.root.parentNode === host) return true;
    if (state.root && state.root.parentNode) state.root.parentNode.removeChild(state.root);
    host.classList.add('cart-item--confirming-remove');
    host.appendChild(state.root);
    state.host = host;
    return true;
  }

  function unmount() {
    if (state.root && state.root.parentNode) state.root.parentNode.removeChild(state.root);
    if (state.host) state.host.classList.remove('cart-item--confirming-remove');
    state.host = null;
  }

  function trapKeydown(event) {
    if (!state.open) return;
    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      close(false);
      return;
    }
    if (event.key !== 'Tab' && event.keyCode !== 9) return;

    var focusable = state.dialog.querySelectorAll(FOCUSABLE_SELECTOR);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    var inside = state.dialog.contains(active);
    if (event.shiftKey) {
      if (active === first || !inside) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !inside) {
      // Plain Tab from outside the dialog re-enters at the top, matching the
      // Shift+Tab branch so focus can never escape while the dialog is open.
      event.preventDefault();
      first.focus();
    }
  }

  function open(productTitle, anchorEl) {
    if (!state.root) buildOverlay();

    var host = resolveHost(anchorEl);
    if (!mountInto(host)) {
      // No sensible anchor → resolve as cancel rather than leaving the
      // caller hanging; the remove flow will skip the confirm gracefully.
      if (state.resolve) {
        var r = state.resolve;
        state.resolve = null;
        r(false);
      }
      return;
    }

    state.titleEl.textContent =
      'Remove your free ' + (productTitle || 'gift') + '? This comes at no extra cost.';

    state.lastActive = document.activeElement;
    state.root.removeAttribute('hidden');
    // Reflow so the opacity/scale transition plays.
    // eslint-disable-next-line no-unused-expressions
    state.root.offsetHeight;
    state.root.classList.add('is-open');
    state.open = true;

    state.keydownHandler = trapKeydown;
    document.addEventListener('keydown', state.keydownHandler, true);

    window.requestAnimationFrame(function () {
      try {
        state.keepBtn.focus();
      } catch (_) {
        /* ignore */
      }
    });
  }

  function close(confirmed) {
    if (!state.open) return;
    state.open = false;
    state.root.classList.remove('is-open');
    state.root.setAttribute('hidden', '');

    if (state.keydownHandler) {
      document.removeEventListener('keydown', state.keydownHandler, true);
      state.keydownHandler = null;
    }

    var resolver = state.resolve;
    state.resolve = null;

    if (state.lastActive && typeof state.lastActive.focus === 'function') {
      try {
        state.lastActive.focus();
      } catch (_) {
        /* ignore */
      }
    }
    state.lastActive = null;

    // Unmount after the transition so reopening on another line doesn't
    // flash the previous overlay.
    window.setTimeout(unmount, 180);

    if (resolver) resolver(!!confirmed);
  }

  window.__confirmFreeGiftRemoval = function (productTitle, anchorEl) {
    if (state.open && state.resolve) {
      var prior = state.resolve;
      state.resolve = null;
      prior(false);
    }
    return new Promise(function (resolve) {
      state.resolve = resolve;
      open(productTitle, anchorEl);
    });
  };
})();
